import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { imgUrl, StatusBadge, AvailabilityBadge } from "../components/PropertyCard";
import { VideoPlayer } from "../components/VideoPlayer";
import { MapPin, Maximize2, Check, ArrowRight, Phone, Calendar, CreditCard, FileDown, Play, X } from "lucide-react";
import { trackListingView, trackDocumentDownload } from "../lib/tracking";

// Detect UUID v4 to know if visitor entered with the legacy /locaux/<uuid> URL → we redirect to /locaux/<slug>
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [videoOpen, setVideoOpen] = useState(null); // index

  useEffect(() => {
    api.get(`/properties/${id}`).then(r => {
      const p = r.data;
      // If user landed via UUID URL but property has a slug, replace URL with SEO-friendly one
      if (p?.slug && UUID_RE.test(id) && id !== p.slug) {
        navigate(`/locaux/${p.slug}`, { replace: true });
        return;
      }
      setProperty(p);
      // Track listing view in GA4/GTM/Ads
      trackListingView({
        id: p.id,
        title: p.title,
        price: p.price || p.monthly_rent,
        city: p.city || p.location,
        type: p.property_type || p.type,
      });
    }).catch(() => setProperty(false));
  }, [id, navigate]);

  if (property === null) return <div className="p-24 text-center font-mono text-sm">Chargement...</div>;
  if (property === false) return <div className="p-24 text-center font-mono text-sm">Local introuvable.</div>;

  const status = property.status || (property.available ? "disponible" : "loue");
  const canReserve = status === "disponible";

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <Link to="/locaux" data-testid="back-to-listings" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 inline-block">← Retour aux locaux</Link>

        <div className="grid lg:grid-cols-12 gap-12 mt-4">
          {/* Gallery + videos */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] bg-white border border-slate-900/15 overflow-hidden">
              {property.images?.[activeImg] && (
                <img src={imgUrl(property.images[activeImg])} alt={property.title} className="w-full h-full object-cover" data-testid="property-main-image" />
              )}
            </div>
            {(property.images?.length > 1 || property.videos?.length > 0) && (
              <div className="grid grid-cols-5 gap-3 mt-3">
                {property.images?.map((img, i) => (
                  <button key={`img-${i}`} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                    className={`aspect-[4/3] overflow-hidden border ${activeImg === i ? "border-slate-900 border-2" : "border-slate-900/15"}`}>
                    <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {property.videos?.map((v, i) => (
                  <button key={`vid-${i}`} onClick={() => setVideoOpen(i)} data-testid={`video-thumb-${i}`}
                    className="aspect-[4/3] border border-slate-900/15 bg-slate-900 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-[#FFC107] border border-slate-900 flex items-center justify-center">
                        <Play className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute bottom-1 left-1 text-white text-[10px] font-bold uppercase">Vidéo</div>
                  </button>
                ))}
              </div>
            )}

            {/* Inline videos viewer */}
            {videoOpen !== null && property.videos?.[videoOpen] && (
              <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setVideoOpen(null)} data-testid="video-modal">
                <button onClick={() => setVideoOpen(null)} className="absolute top-6 right-6 text-white border border-white w-10 h-10 flex items-center justify-center hover:bg-white hover:text-black"><X className="w-5 h-5"/></button>
                <div className="w-full max-w-5xl aspect-video bg-black" onClick={e => e.stopPropagation()}>
                  <VideoPlayer video={property.videos[videoOpen]} className="w-full h-full" autoplay controls />
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— Bâtiment d'activité</div>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[0.95]">{property.title}</h1>
              <StatusBadge status={status} />
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {status === "disponible" && <AvailabilityBadge available_from={property.available_from} />}
              <span className="tag flex items-center gap-1.5"><Maximize2 className="w-3 h-3"/>{property.surface_min}–{property.surface_max} m²</span>
              <span className="tag flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{property.location}</span>
              {property.modular && <span className="tag bg-[#FFC107]">Modulable</span>}
            </div>

            <div className="bg-white border border-slate-900/15 p-6 mb-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Loyer mensuel HT</div>
              <div className="font-display font-black text-4xl mt-1">{property.price_monthly.toLocaleString("fr-FR")} €</div>
              <div className="text-xs text-slate-500 mt-1">Charges et taxes en sus</div>
            </div>

            <p className="text-slate-700 leading-relaxed mb-6">{property.description}</p>

            <div className="grid grid-cols-2 gap-2 mb-8">
              {property.features?.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#FFC107] shrink-0" strokeWidth={3} />
                  <span>{f}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <a href={`${API}/properties/${property.id}/pdf`} target="_blank" rel="noreferrer" data-testid="cta-download-pdf"
                onClick={() => trackDocumentDownload({ name: `fiche-${property.slug || property.id}.pdf`, url: `${API}/properties/${property.id}/pdf` })}
                 className="btn-brutal bg-slate-900 text-white border border-slate-900 w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-between">
                <span className="flex items-center gap-3"><FileDown className="w-4 h-4"/>Télécharger la fiche PDF</span>
                <ArrowRight className="w-4 h-4" />
              </a>

              {canReserve ? (
                <>
                  <Link to={`/reserver/${property.id}?type=paid`} data-testid="cta-reserve-paid"
                    className="btn-brutal btn-brutal-solar w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-between">
                    <span className="flex items-center gap-3"><CreditCard className="w-4 h-4"/>Réserver avec acompte</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to={`/reserver/${property.id}?type=visit`} data-testid="cta-reserve-visit"
                    className="btn-brutal bg-white border border-slate-900 w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-between">
                    <span className="flex items-center gap-3"><Calendar className="w-4 h-4"/>Demander une visite</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to={`/reserver/${property.id}?type=contact`} data-testid="cta-reserve-contact"
                    className="btn-brutal bg-white border border-slate-900 w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-between">
                    <span className="flex items-center gap-3"><Phone className="w-4 h-4"/>Être rappelé</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              ) : (
                <div className="bg-slate-100 border border-slate-900/15 p-4 text-sm" data-testid="not-available-notice">
                  <strong>{status === "loue" ? "Local actuellement loué" : "Local occupé"}.</strong> Vous pouvez tout de même nous contacter pour être informé d'une disponibilité future ou explorer un autre local.
                  <Link to="/demande" className="block mt-3 text-center btn-brutal btn-brutal-solar px-4 py-2 font-bold uppercase tracking-wider text-xs">Faire une demande personnalisée</Link>
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-900/10 text-sm text-slate-600">
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-1 text-slate-900 shrink-0"/><span>{property.address}</span></div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
